/**
 * metaLeadGen/useCrmFunnelData.ts
 *
 * Fetches real CRM funnel metrics from /api/crm-funnel (MongoDB-backed).
 * Replaces mock data in the unified funnel service.
 *
 * Funnel definition:
 *   Applications  = Application collection (Typeform submissions), by createdAt
 *   MQLs          = EOC records where the prospect showed up (not no-show/cancelled)
 *   Closes        = EOC records with a positive closing outcome
 *   Cash          = sum of cashCollected on closing EOC records
 */

import { useState, useEffect, useRef } from "react";

export interface CrmPeriodMetrics {
  applications: number;
  mqls: number;
  closes: number;
  cashCollected: number;
}

export interface CrmFunnelResult {
  current: CrmPeriodMetrics;
  previous: CrmPeriodMetrics;
  loading: boolean;
  error: string | null;
}

const EMPTY: CrmPeriodMetrics = { applications: 0, mqls: 0, closes: 0, cashCollected: 0 };

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function useCrmFunnelData(
  startDate: string,
  endDate: string,
): CrmFunnelResult {
  const [current,  setCurrent]  = useState<CrmPeriodMetrics>(EMPTY);
  const [previous, setPrevious] = useState<CrmPeriodMetrics>(EMPTY);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // Stable ref to avoid stale closure in cleanup
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) return;

    // Calculate previous period (same duration, immediately before)
    const startMs   = new Date(startDate + "T00:00:00Z").getTime();
    const endMs     = new Date(endDate   + "T00:00:00Z").getTime();
    const duration  = Math.round((endMs - startMs) / 86_400_000) + 1; // days inclusive
    const prevEnd   = addDays(startDate, -1);
    const prevStart = addDays(prevEnd, -(duration - 1));

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      startDate,
      endDate,
      prevStart,
      prevEnd,
    });

    fetch(`/api/crm-funnel?${params}`, { signal: ctrl.signal })
      .then(r => r.json() as Promise<{ current: CrmPeriodMetrics; previous: CrmPeriodMetrics }>)
      .then(data => {
        setCurrent(data.current);
        setPrevious(data.previous);
        setLoading(false);
      })
      .catch(err => {
        if ((err as Error).name === "AbortError") return;
        setError(String(err));
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [startDate, endDate]);

  return { current, previous, loading, error };
}
